import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { createWholesaleOrder } from '../../api/services';
import SafeImage from '../ui/SafeImage';
import { formatPrice } from '../../utils/format';

// Precios netos: se discrimina IVA 21%. Transporte obligatorio.
const IVA_RATE = 0.21;
const TRANSPORTES = ['Vía Cargo', 'Oca', 'Andreani', 'Correo Argentino', 'Otro'];

/**
 * Pedido mayorista. Sin pago por ahora: se confirma y queda registrado para
 * que el asesor lo coordine. El cliente elige el transporte.
 */
export default function CartDrawer() {
  const { items, isOpen, setIsOpen, subtotal, updateQty, removeItem, keyOf, clearCart } = useCart();
  const [sending, setSending] = useState(false);
  const [transport, setTransport] = useState('');
  const [transportOtro, setTransportOtro] = useState('');
  const navigate = useNavigate();

  const carrier = transport === 'Otro' ? transportOtro.trim() : transport;
  const iva = Math.round(subtotal * IVA_RATE);
  const total = subtotal + iva;

  const confirmar = async () => {
    if (!carrier) return toast.error('Elegí un transporte para el pedido');
    setSending(true);
    try {
      const order = await createWholesaleOrder(
        items.map((i) => ({ product: i.product, variantId: i.variantId, quantity: i.quantity })),
        '',
        carrier
      );
      clearCart();
      setTransport('');
      setTransportOtro('');
      setIsOpen(false);
      toast.success('¡Pedido enviado! Te contactamos para coordinarlo.');
      navigate(`/mayorista/pedido/${order._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo enviar el pedido');
    }
    setSending(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-mist-50 shadow-2xl dark:bg-ink"
          >
            <div className="flex items-center justify-between border-b border-ink/10 px-6 py-5 dark:border-white/10">
              <h3 className="font-serif text-xl">Tu pedido ({items.length})</h3>
              <button onClick={() => setIsOpen(false)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-ink/5">
                <X size={20} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <ShoppingBag size={48} className="text-ink/20" />
                <p className="text-ink/50">Todavía no agregaste productos</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {items.map((item) => (
                    <div key={keyOf(item)} className="flex gap-4 border-b border-ink/5 py-4 last:border-0 dark:border-white/5">
                      <SafeImage src={item.image} seed={item.slug} alt={item.name} className="h-24 w-20 flex-shrink-0 rounded-xl" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex justify-between gap-2">
                          <div>
                            <p className="font-serif text-base leading-tight">{item.name}</p>
                            <p className="text-xs text-ink/50">
                              {[item.color, item.size].filter(Boolean).join(' · ')}
                            </p>
                            {item.sku && <p className="font-mono text-[11px] text-ink/30">{item.sku}</p>}
                          </div>
                          <button onClick={() => removeItem(keyOf(item))} className="text-ink/30 transition hover:text-brand-500">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center gap-2 rounded-full border border-ink/15 px-1 dark:border-white/15">
                            <button onClick={() => updateQty(keyOf(item), item.quantity - 1)} className="grid h-7 w-7 place-items-center rounded-full hover:bg-ink/5"><Minus size={13} /></button>
                            <span className="w-5 text-center text-sm">{item.quantity}</span>
                            <button onClick={() => updateQty(keyOf(item), item.quantity + 1)} className="grid h-7 w-7 place-items-center rounded-full hover:bg-ink/5"><Plus size={13} /></button>
                          </div>
                          <span className="text-sm font-medium">{formatPrice(item.price * item.quantity)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 border-t border-ink/10 px-6 py-5 dark:border-white/10">
                  {/* Transporte (obligatorio) */}
                  <div>
                    <label className="label">Transporte</label>
                    <select className="input" value={transport} onChange={(e) => setTransport(e.target.value)}>
                      <option value="">Elegí un transporte...</option>
                      {TRANSPORTES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {transport === 'Otro' && (
                      <input className="input mt-2" value={transportOtro}
                        onChange={(e) => setTransportOtro(e.target.value)}
                        placeholder="Nombre del expreso" />
                    )}
                  </div>

                  {/* Totales con IVA discriminado (precios netos) */}
                  <div className="space-y-1 border-t border-ink/10 pt-3 text-sm dark:border-white/10">
                    <div className="flex justify-between text-ink/60 dark:text-mist-100/60">
                      <span>Subtotal (sin IVA)</span><span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-ink/60 dark:text-mist-100/60">
                      <span>IVA 21%</span><span>{formatPrice(iva)}</span>
                    </div>
                    <div className="flex justify-between pt-1 text-lg font-medium">
                      <span>Total</span><span>{formatPrice(total)}</span>
                    </div>
                  </div>

                  <p className="text-xs text-ink/50">La forma de pago se coordina con tu asesor.</p>
                  <button disabled={sending || !carrier} onClick={confirmar} className="btn-primary w-full">
                    {sending ? 'Enviando...' : 'Confirmar pedido'}
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
